import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  constants,
  NotificationManager,
  PaymentsFormsApi,
  PaymentV2Api,
  useApplicationState,
} from '@atlas/core';
import { DefaultPopup, Loader } from '@atlas/ui';

import PaymentsForm from 'src/common/Popup/PaymentsFormPopup/PaymentsForm';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';
import PaymentListItem from 'src/components/ClientProfile/Payment/PaymentListItem';

const PaymentsFormPopup = ({
  data: { amount, formId, interpolationValues = {}, paymentMethod, publicPaymentId, type },
  id,
}) => {
  const {
    authenticated,
    resolved,
    wallet: { currency },
  } = useApplicationState();
  const { redirectToSignIn } = useSignInRedirects();
  const history = useHistory();
  const [formConfig, setFormConfig] = useState(null);
  const ref = useRef(null);
  const [hasScroll, setHasScroll] = useState(false);

  const isDeposit = type === constants.PAYMENT_OPERATION.DEPOSIT;

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  useEffect(() => {
    PaymentsFormsApi.getPaymentsForms(formId)
      .then(formConfig => {
        setFormConfig(formConfig);
      })
      .catch(e => {
        handleClose();
        NotificationManager.display({
          data: {
            text: e.message,
            title: 'payments_form_error_title',
          },
          type: 'paymentFormError',
        });
      });
  }, [formId, handleClose]);

  useEffect(() => {
    const contentWrapper = ref?.current;

    if (!contentWrapper) return;

    const updateScrollState = () => {
      setHasScroll(contentWrapper.scrollHeight > contentWrapper.clientHeight);
    };

    updateScrollState();

    const observer = new MutationObserver(() => {
      updateScrollState();
    });

    observer.observe(contentWrapper, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  const handleSubmit = useCallback(
    values => {
      const payload = {
        additionalParameters: {
          ...values,
          publicPaymentId,
        },
        amount,
        currency,
        pmId: paymentMethod.id,
      };

      const request = isDeposit
        ? PaymentV2Api.createAsyncDeposit
        : PaymentV2Api.createAsyncWithdrawalV2;

      request(payload).then(response => {
        handleClose();

        if (isDeposit) return;

        NotificationManager.display({
          data: {
            amount,
            currency,
            publicPaymentId: response?.publicPaymentId,
            paymentMethod,
          },
          message: 'successfulWithdrawal',
          type: 'successfulWithdrawal',
        });
      });
    },
    [amount, currency, handleClose, isDeposit, paymentMethod, publicPaymentId],
  );

  useEffect(() => {
    if (resolved && !authenticated) {
      const { pathname } = history.location;

      handleClose();
      redirectToSignIn({ redirectPath: pathname });
    }
  }, [resolved, authenticated, history, redirectToSignIn, handleClose]);

  return (
    <DefaultPopup
      className="payments-form"
      headerTitle={isDeposit ? 'deposit' : 'withdrawal'}
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className={cx('payments-form-content', { 'with-scroll': hasScroll })} ref={ref}>
        {formConfig ? (
          <>
            <PaymentListItem payment={paymentMethod} />
            <PaymentsForm
              formConfig={formConfig}
              interpolationValues={interpolationValues}
              onSubmit={handleSubmit}
            />
          </>
        ) : (
          <Loader />
        )}
      </div>
    </DefaultPopup>
  );
};

PaymentsFormPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    formId: PropTypes.number.isRequired,
    paymentMethod: PropTypes.object.isRequired,
    publicPaymentId: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
};

export default PaymentsFormPopup;
